import React, { useMemo } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setOverBoxRoundValue, setState } from "./defineSlice";
import {
	removeComma,
	isDigitsOnlyNumericInput,
	hasNaNOverBoxRoundValues,
} from "./defineUtils";

export default function OverBoxRounderDialog({ open, onClose, onApply, disabled }) {
	const dispatch = useDispatch();
	const { listProduct, overBoxRoundValues } = useSelector((state) => state.define);

	const skuList = useMemo(
		() =>
			(listProduct ?? []).filter(
				(item) =>
					item.packGroupOrSKUName !== "All" &&
					item.packGroupOrSKUID !== 0 &&
					item.packGroupOrSKUID !== undefined
			),
		[listProduct]
	);

	const handleSkuRoundValueChange = (skuId, rawValue) => {
		const value = removeComma(rawValue);
		if (!isDigitsOnlyNumericInput(value)) {
			dispatch(setState("isError", true));
			dispatch(setState("isError_Info", "Only numbers are allowed."));
			return;
		}
		dispatch(setOverBoxRoundValue({ skuId, value }));
	};

	const handleApplyClick = () => {
		if (hasNaNOverBoxRoundValues(overBoxRoundValues)) {
			dispatch(setState("isError", true));
			dispatch(setState("isError_Info", "Only numbers are allowed."));
			return;
		}
		onApply();
	};

	const getDisplayValue = (skuId) => {
		const value =
			overBoxRoundValues?.[skuId] ?? overBoxRoundValues?.[String(skuId)];
		if (value === undefined || value === null || value === "") {
			return "";
		}
		if (Number.isNaN(Number(value))) {
			return "";
		}
		return Number(value).toLocaleString("en-US");
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Typography
					sx={{
						color: "darkred.light",
						fontWeight: "700",
						fontSize: "18px !important",
					}}
				>
					OverBox Rounder Value per SKU
				</Typography>
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						position: "absolute",
						right: 8,
						top: 8,
						color: "darkred.light",
					}}
				>
					<Close />
				</IconButton>
			</DialogTitle>
			<DialogContent
				dividers
				sx={{
					pt: "12px !important",
					maxHeight: "60vh",
					overflowY: "auto",
				}}
			>
				{skuList.length === 0 ? (
					<Typography color="text.secondary">
						No SKUs available for this scenario.
					</Typography>
				) : (
					skuList.map((sku) => (
						<Box
							key={sku.packGroupOrSKUID}
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 2,
								mb: 2,
							}}
						>
							<Typography
								sx={{
									flex: 1,
									minWidth: 0,
									fontWeight: 500,
								}}
							>
								{sku.packGroupOrSKUName}
							</Typography>
							<TextField
								variant="outlined"
								value={getDisplayValue(sku.packGroupOrSKUID)}
								onChange={(e) =>
									handleSkuRoundValueChange(
										sku.packGroupOrSKUID,
										e.target.value
									)
								}
								size="small"
								sx={{ width: 200, flexShrink: 0 }}
								disabled={disabled}
							/>
						</Box>
					))
				)}
			</DialogContent>
			<DialogActions sx={{ padding: "12px 24px !important" }}>
				<Button
					onClick={onClose}
					sx={{
						bgcolor: "grey.lighter",
						color: "darkred.light",
						boxShadow: "none",
						"&:hover": {
							bgcolor: "darkred.main",
							color: "white.main",
							border: "none",
						},
					}}
				>
					Cancel
				</Button>
				<Button
					sx={{
						bgcolor: "darkred.light",
						color: "white.main",
						fontWeight: "700",
						textTransform: "none",
						"&:hover": {
							bgcolor: "darkred.main",
						},
					}}
					variant="contained"
					disabled={disabled}
					onClick={handleApplyClick}
				>
					Apply
				</Button>
			</DialogActions>
		</Dialog>
	);
}
